import { FieldArray, type FieldArrayRenderProps, setIn, setNestedObjectValues } from "formik";

import { InputEmail } from "commonComponents/entity/email/InputEmail/InputEmail";
import { Email } from "commonComponents/entity/email/InputEmail/InputEmail.types";
import { InputEmailCreateNew } from "commonComponents/entity/email/InputEmailCreateNew/InputEmailCreateNew";
import { type IInputEmailListProps } from "commonComponents/entity/email/InputEmailList/InputEmailList.types";

/**
 * Component encapsulating a list of InputEmails, including the ability to add and delete
 * individual inputs
 */
export const InputEmailList: React.FC<IInputEmailListProps> = ({
    id,
    "data-testid": dataTestId,
    isReadOnly = false,
    canDelete = false,
    addEmailLabel = "Add Email",
    emailInputLabel,
    labelInputLabel,
    gutterWidth = 8,
    columnLayout = "auto",
    deletePosition = "inside",
    maxCount,
    value,
    onAdd,
    onChange,
    onBlur,
    onDelete,
}) => {
    const handleAdd =
        ({ form: { submitCount, touched, setTouched }, push }: FieldArrayRenderProps) =>
        () => {
            const newIndex = value.length;
            const newId = `${id}[${newIndex}]`;

            if (onAdd) {
                onAdd(newId, newIndex, push);
            } else {
                const newValue = new Email();
                push(newValue);
                if (submitCount) {
                    setTouched(setIn(touched, newId, setNestedObjectValues(newValue, true)));
                }
            }
        };

    const handleDelete =
        (index: number, { remove }: FieldArrayRenderProps) =>
        (deletedId: string) => {
            onDelete ? onDelete(deletedId, index, remove) : remove(index);
        };

    return (
        <FieldArray name={id}>
            {(renderProps) => {
                return [
                    ...value.map((email, index) => {
                        return (
                            <InputEmail
                                key={email.key}
                                id={`${id}[${index}]`}
                                data-testid={`${dataTestId}[${index}]`}
                                isReadOnly={isReadOnly}
                                canDelete={canDelete}
                                emailInputLabel={emailInputLabel}
                                labelInputLabel={labelInputLabel}
                                gutterWidth={gutterWidth}
                                columnLayout={columnLayout}
                                deletePosition={deletePosition}
                                value={email}
                                onChange={onChange}
                                onBlur={onBlur}
                                onDelete={handleDelete(index, renderProps)}
                            />
                        );
                    }),
                    (maxCount === undefined || value.length < maxCount) && !isReadOnly && (
                        <InputEmailCreateNew
                            key="addEmail"
                            data-testid={`${dataTestId}-addEmail`}
                            onClick={handleAdd(renderProps)}
                        >
                            {addEmailLabel}
                        </InputEmailCreateNew>
                    ),
                ];
            }}
        </FieldArray>
    );
};
